import React, { useState } from 'react';
import {
    Stack,
    Typography,
    Box,
    Avatar,
    TextField,
    InputAdornment,
    IconButton,
    InputLabel,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import FormInput from '../inputs/FormInput';
import SwitchInput from '../inputs/SwitchInput';

const SECRET_KEY_HINTS = ['secret', 'password', 'api_key', 'apikey', 'token', 'private_key'];

const isBilingual = (val) =>
    val !== null &&
    typeof val === 'object' &&
    !Array.isArray(val) &&
    ('ar' in val || 'en' in val);

const looksLikeImageUrl = (val) =>
    typeof val === 'string' &&
    /^https?:\/\//.test(val) &&
    /\.(png|jpe?g|svg|webp|gif|avif)(\?|$)/i.test(val);

const looksLikeUrl = (key) => /(_url|_link|website|webhook)$/i.test(key);

const looksSensitive = (key) => SECRET_KEY_HINTS.some((h) => key.toLowerCase().includes(h));

const humanize = (key) =>
    key
        .replace(/[_.]/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim();

const inferType = (key, value) => {
    if (typeof value === 'boolean') return 'boolean';
    if (isBilingual(value)) return 'bilingual';
    if (typeof value === 'number') return 'number';
    if (looksSensitive(key)) return 'secret';
    if (looksLikeImageUrl(value)) return 'image';
    if (looksLikeUrl(key)) return 'url';
    if (typeof value === 'string' && value.length > 80) return 'textarea';
    return 'text';
};

/**
 * Tiny outer label used for fields that don't render their own (bilingual + image).
 */
const FieldLabel = ({ children, htmlFor }) => (
    <InputLabel
        htmlFor={htmlFor}
        sx={{ fontWeight: 'bold', mb: 0.5, color: 'text.primary' }}
    >
        {children}
    </InputLabel>
);

const SettingFieldRenderer = ({
    fieldKey,
    value,
    onChange,
    type,
    label,
    helperText,
    disabled,
    activeLang,
}) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const [showSecret, setShowSecret] = useState(false);

    const resolvedType = type || inferType(fieldKey, value);
    const resolvedLabel = label || humanize(fieldKey);

    if (resolvedType === 'boolean') {
        return (
            <SwitchInput
                label={resolvedLabel}
                name={fieldKey}
                checked={Boolean(value)}
                onChange={(e) => onChange(e.target.checked)}
                disabled={disabled}
                helperText={helperText}
            />
        );
    }

    if (resolvedType === 'bilingual') {
        const safeVal = value && typeof value === 'object' ? value : {};

        // When the parent SettingsPage drives a single LanguageToggle, render
        // only the active language's input so the form follows the same
        // pattern as the bilingual content forms (Brands, Categories, etc.).
        // When activeLang is undefined we fall back to showing both inputs
        // side-by-side (legacy behavior).
        if (activeLang === 'ar' || activeLang === 'en') {
            const langTag = activeLang === 'ar' ? 'AR' : 'EN';
            return (
                <Box>
                    <FieldLabel>
                        {resolvedLabel}
                        <Typography
                            component="span"
                            variant="caption"
                            color="primary.main"
                            sx={{ ml: 1, fontWeight: 600 }}
                        >
                            ({langTag})
                        </Typography>
                    </FieldLabel>
                    <FormInput
                        name={`${fieldKey}.${activeLang}`}
                        value={safeVal[activeLang] || ''}
                        onChange={(e) =>
                            onChange({ ...safeVal, [activeLang]: e.target.value })
                        }
                        disabled={disabled}
                        required={false}
                        placeholder=""
                    />
                    {helperText && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {helperText}
                        </Typography>
                    )}
                </Box>
            );
        }

        return (
            <Box>
                <FieldLabel>{resolvedLabel}</FieldLabel>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Box sx={{ flex: 1 }}>
                        <FormInput
                            label="EN"
                            name={`${fieldKey}.en`}
                            value={safeVal.en || ''}
                            onChange={(e) => onChange({ ...safeVal, en: e.target.value })}
                            disabled={disabled}
                            required={false}
                            placeholder=""
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <FormInput
                            label="AR"
                            name={`${fieldKey}.ar`}
                            value={safeVal.ar || ''}
                            onChange={(e) => onChange({ ...safeVal, ar: e.target.value })}
                            disabled={disabled}
                            required={false}
                            placeholder=""
                        />
                    </Box>
                </Stack>
                {helperText && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {helperText}
                    </Typography>
                )}
            </Box>
        );
    }

    if (resolvedType === 'image') {
        return (
            <Box>
                <FieldLabel>{resolvedLabel}</FieldLabel>
                <Stack direction="row" spacing={2} alignItems="center">
                    {value && (
                        <Avatar
                            src={value}
                            variant="rounded"
                            sx={{
                                width: 64,
                                height: 64,
                                bgcolor: 'background.default',
                                border: (t) => `1px solid ${t.palette.divider}`,
                                flexShrink: 0,
                            }}
                        />
                    )}
                    <Box sx={{ flex: 1 }}>
                        <FormInput
                            type="link"
                            name={fieldKey}
                            value={value || ''}
                            onChange={(e) => onChange(e.target.value)}
                            disabled={disabled}
                            required={false}
                            placeholder="https://..."
                        />
                    </Box>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {helperText || (isArabic ? 'رابط الصورة (URL)' : 'Image URL')}
                </Typography>
            </Box>
        );
    }

    if (resolvedType === 'secret') {
        // Existing inputs/PasswordInput.jsx is uncontrolled (no `value` prop), so
        // for editing a server-loaded secret we keep a controlled MUI TextField
        // with a manual eye-toggle.
        return (
            <Box>
                <FieldLabel>{resolvedLabel}</FieldLabel>
                <TextField
                    fullWidth
                    size="small"
                    type={showSecret ? 'text' : 'password'}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={disabled}
                    helperText={helperText}
                    autoComplete="new-password"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={() => setShowSecret((s) => !s)}
                                    edge="end"
                                    tabIndex={-1}
                                >
                                    {showSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
        );
    }

    if (resolvedType === 'textarea') {
        return (
            <FormInput
                type="textarea"
                label={resolvedLabel}
                name={fieldKey}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                required={false}
                helperText={helperText}
                placeholder=""
            />
        );
    }

    if (resolvedType === 'number') {
        return (
            <FormInput
                type="number"
                label={resolvedLabel}
                name={fieldKey}
                value={value ?? ''}
                onChange={(e) => {
                    const v = e.target.value;
                    onChange(v === '' ? null : Number(v));
                }}
                disabled={disabled}
                required={false}
                helperText={helperText}
                placeholder=""
            />
        );
    }

    if (resolvedType === 'url') {
        return (
            <FormInput
                type="link"
                label={resolvedLabel}
                name={fieldKey}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                required={false}
                helperText={helperText}
                placeholder="https://..."
            />
        );
    }

    return (
        <FormInput
            label={resolvedLabel}
            name={fieldKey}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            required={false}
            helperText={helperText}
            placeholder=""
        />
    );
};

export default SettingFieldRenderer;
